import React, { useEffect, useState } from "react";
import { Tv as TvIcon, Plus, Trash2, RefreshCw, ArrowUp, ArrowDown, Film, Image as ImageIcon, X, ExternalLink, Lock, Unlock, Clock, Palette } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

const TARGETS = [
  { key: "both", label: "TV + web" },
  { key: "tv",   label: "TV only" },
  { key: "web",  label: "Web only" },
];

const RAIL_KINDS = [
  { key: "regular",     label: "Standard rail" },
  { key: "top10",       label: "Top 10 (numbered)" },
  { key: "coming_soon", label: "Coming Soon (countdowns)" },
  { key: "brand_hub",   label: "Brand Hub (themed collection tile)" },
  { key: "live_now",    label: "Live Now (auto-populated)" },
];

/**
 * TvAppAdminTab — admin-managed content on the Smart-TV / web home:
 *   · Hero banners (rotating full-bleed slides)
 *   · Featured rails (curated horizontal rails)
 *   · Coming Soon drops (scheduled releases with countdowns)
 */
export default function TvAppAdminTab() {
  const [sub, setSub] = useState("banners");
  return (
    <div data-testid="admin-tv-tab" className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-display font-black text-2xl text-white flex items-center gap-2">
          <TvIcon className="w-5 h-5 text-tv-accent" /> TV App
        </h2>
        <div className="flex items-center gap-2 flex-wrap">
          {[
            { k: "banners",     label: "Hero banners" },
            { k: "rails",       label: "Featured rails" },
            { k: "coming_soon", label: "Coming Soon" },
          ].map((t) => (
            <button
              key={t.k}
              data-testid={`admin-tv-sub-${t.k}`}
              onClick={() => setSub(t.k)}
              className={`text-xs font-bold px-3 py-1.5 rounded-full ${sub === t.k ? "bg-tv-accent text-black" : "bg-white/10 text-white/70 hover:bg-white/20"}`}
            >{t.label}</button>
          ))}
          <a
            href="/tv?admin=1"
            target="_blank"
            rel="noreferrer"
            data-testid="admin-tv-view-link"
            className="text-xs font-bold px-3 py-1.5 rounded-full bg-tv-live text-white hover:bg-red-600 inline-flex items-center gap-1.5"
          >
            <ExternalLink className="w-3 h-3" /> View TV app (edit mode)
          </a>
        </div>
      </div>

      {sub === "banners" && <HeroBannersPane />}
      {sub === "rails" && <FeaturedRailsPane />}
      {sub === "coming_soon" && <ComingSoonPane />}
    </div>
  );
}

// ── Hero banners ───────────────────────────────────────────────────────
function HeroBannersPane() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(emptyHero());

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/tv/hero");
      setRows(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title.trim() || !form.image_url.trim()) {
      toast.error("Title and image URL required"); return;
    }
    try {
      await api.post("/admin/tv/hero", { ...form, order: Number(form.order) || 0 });
      toast.success("Hero banner added"); setAdding(false); setForm(emptyHero()); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const patch = async (row, body) => {
    try { await api.patch(`/admin/tv/hero/${row.id}`, body); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (row) => {
    if (!window.confirm(`Delete "${row.title}"?`)) return;
    try { await api.delete(`/admin/tv/hero/${row.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-3" data-testid="admin-tv-banners-pane">
      <div className="flex items-center gap-2">
        <div className="text-white/60 text-sm flex-1">Banners rotate at the top of TV + web home. Toggle to limit to TV only for a Smart-TV push.</div>
        <button onClick={load} className="text-white/60 hover:text-white text-sm inline-flex items-center gap-1"><RefreshCw className="w-3.5 h-3.5" /></button>
        <button
          data-testid="admin-tv-banner-add-open"
          onClick={() => setAdding((v) => !v)}
          className="bg-tv-accent hover:bg-yellow-400 text-black font-black text-xs px-3 py-1.5 rounded inline-flex items-center gap-1"
        ><Plus className="w-3.5 h-3.5" /> New banner</button>
      </div>

      {adding && (
        <div className="bg-white/5 border border-white/10 rounded-lg p-3 space-y-2" data-testid="admin-tv-banner-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <input data-testid="banner-title" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} />
            <input placeholder="Subtitle" value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} className={inputCls} />
            <input data-testid="banner-image" placeholder="Image URL (16:9, 1920+ wide)" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className={inputCls} />
            <input placeholder="Trailer URL (optional, muted-autoplay)" value={form.trailer_url} onChange={(e) => setForm({ ...form, trailer_url: e.target.value })} className={inputCls} />
            <input placeholder="Watch URL (where Play sends)" value={form.watch_url} onChange={(e) => setForm({ ...form, watch_url: e.target.value })} className={inputCls} />
            <input placeholder='Badge (e.g. "NEW", "TAP IN EXCLUSIVE")' value={form.badge} onChange={(e) => setForm({ ...form, badge: e.target.value })} className={inputCls} />
          </div>
          <textarea placeholder="Description" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} />
          <div className="flex items-center gap-3 flex-wrap">
            <label className="inline-flex items-center gap-1.5 text-sm">
              Target
              <select data-testid="banner-target" value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} className="bg-black/40 border border-white/10 rounded px-2 py-1 text-xs">
                {TARGETS.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </label>
            <label className="inline-flex items-center gap-1.5 text-sm">
              Order <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="w-20 bg-black/40 border border-white/10 rounded px-2 py-1 text-xs" />
            </label>
            <label className="inline-flex items-center gap-1.5 text-sm">
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="accent-tv-accent" /> Active
            </label>
            <label className="inline-flex items-center gap-1.5 text-sm" title="Hide this banner from free-tier viewers">
              <input data-testid="banner-paid" type="checkbox" checked={!!form.is_paid_only} onChange={(e) => setForm({ ...form, is_paid_only: e.target.checked })} className="accent-tv-accent" />
              <Lock className="w-3 h-3" /> Paid only
            </label>
            <label className="inline-flex items-center gap-1.5 text-sm" title="Show in Kids mode (strict allowlist)">
              <input data-testid="banner-kids-safe" type="checkbox" checked={!!form.is_kids_safe} onChange={(e) => setForm({ ...form, is_kids_safe: e.target.checked })} className="accent-emerald-400" />
              🧒 Kids-safe
            </label>
            <button data-testid="banner-save" onClick={create} className="ml-auto bg-emerald-500 hover:bg-emerald-400 text-black font-black text-sm px-4 py-1.5 rounded">Save banner</button>
            <button onClick={() => { setAdding(false); setForm(emptyHero()); }} className="bg-white/5 hover:bg-white/15 text-white/70 text-sm px-3 py-1.5 rounded">Cancel</button>
          </div>
        </div>
      )}

      {loading ? <div className="text-white/60">Loading…</div> : (
        <div className="space-y-2">
          {rows.length === 0 && <div className="text-white/40 text-center py-6 text-sm">No banners yet. Add one so the TV home has a hero.</div>}
          {rows.map((r) => (
            <div key={r.id} data-testid={`admin-tv-banner-${r.id}`} className="bg-white/5 border border-white/10 rounded-lg p-3 flex items-center gap-3">
              <div className="w-32 h-20 bg-black rounded overflow-hidden shrink-0 flex items-center justify-center text-white/30">
                {r.image_url
                  ? <img src={r.image_url} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                  : <ImageIcon className="w-5 h-5" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <div className="font-bold truncate">{r.title}</div>
                  <span className="text-[9px] bg-white/10 text-white/70 px-1.5 py-0.5 rounded uppercase font-black tracking-wider">{r.target || "both"}</span>
                  {r.badge && <span className="text-[9px] bg-tv-accent text-black px-1.5 py-0.5 rounded font-black uppercase">{r.badge}</span>}
                </div>
                <div className="text-white/50 text-xs truncate">{r.subtitle || r.description}</div>
                <div className="text-white/30 text-[10px] mt-0.5">order {r.order} · {r.is_active ? "active" : "inactive"}</div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <label className="inline-flex items-center gap-1 text-xs">
                  <input type="checkbox" checked={!!r.is_active} onChange={(e) => patch(r, { is_active: e.target.checked })} className="accent-tv-accent" /> on
                </label>
                <select value={r.target || "both"} onChange={(e) => patch(r, { target: e.target.value })} className="bg-black/40 border border-white/10 rounded text-xs px-1.5 py-1">
                  {TARGETS.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
                <button onClick={() => patch(r, { order: (r.order || 0) - 1 })} className="p-1 hover:bg-white/10 rounded" title="Move up"><ArrowUp className="w-3.5 h-3.5" /></button>
                <button onClick={() => patch(r, { order: (r.order || 0) + 1 })} className="p-1 hover:bg-white/10 rounded" title="Move down"><ArrowDown className="w-3.5 h-3.5" /></button>
                <button data-testid={`admin-tv-banner-del-${r.id}`} onClick={() => del(r)} className="p-1 text-red-300 hover:text-red-100"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Featured rails ─────────────────────────────────────────────────────
function FeaturedRailsPane() {
  const [rails, setRails] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(emptyRail());
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [r, v] = await Promise.all([
        api.get("/admin/tv/featured-rails"),
        api.get("/videos?limit=200").catch(() => ({ data: [] })),
      ]);
      setRails(r.data || []); setVideos(v.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title.trim()) { toast.error("Rail title required"); return; }
    try {
      await api.post("/admin/tv/featured-rails", { ...form, order: Number(form.order) || 0 });
      toast.success("Rail added"); setAdding(false); setForm(emptyRail()); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const patch = async (row, body) => {
    try { await api.patch(`/admin/tv/featured-rails/${row.id}`, body); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (row) => {
    if (!window.confirm(`Delete rail "${row.title}"?`)) return;
    try { await api.delete(`/admin/tv/featured-rails/${row.id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const filteredVideos = videos.filter((v) => !search || (v.title || "").toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-3" data-testid="admin-tv-rails-pane">
      <div className="flex items-center gap-2">
        <div className="text-white/60 text-sm flex-1">Featured rails show on TV home between system rails. Build a rail, pick videos, reorder them.</div>
        <button
          data-testid="admin-tv-rail-add-open"
          onClick={() => setAdding((v) => !v)}
          className="bg-tv-accent hover:bg-yellow-400 text-black font-black text-xs px-3 py-1.5 rounded inline-flex items-center gap-1"
        ><Plus className="w-3.5 h-3.5" /> New rail</button>
      </div>

      {adding && (
        <div className="bg-white/5 border border-white/10 rounded-lg p-3 space-y-2" data-testid="admin-tv-rail-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <input data-testid="rail-title" placeholder='Title (e.g. "Indie Summer Flicks")' value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} />
            <input placeholder="Subtitle (optional)" value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} className={inputCls} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <label className="block">
              <div className="text-[9px] uppercase text-white/50 font-black mb-0.5">Kind</div>
              <select data-testid="rail-kind" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className={inputCls}>
                {RAIL_KINDS.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
              </select>
            </label>
            {form.kind === "brand_hub" && (<>
              <label className="block">
                <div className="text-[9px] uppercase text-white/50 font-black mb-0.5 flex items-center gap-1"><ImageIcon className="w-3 h-3" /> Cover image URL</div>
                <input data-testid="rail-cover" placeholder="https://…" value={form.cover_image_url} onChange={(e) => setForm({ ...form, cover_image_url: e.target.value })} className={inputCls} />
              </label>
              <label className="block">
                <div className="text-[9px] uppercase text-white/50 font-black mb-0.5 flex items-center gap-1"><Palette className="w-3 h-3" /> Brand color (hex)</div>
                <input data-testid="rail-brand-color" placeholder="#8b5cf6" value={form.brand_color} onChange={(e) => setForm({ ...form, brand_color: e.target.value })} className={inputCls} />
              </label>
            </>)}
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <label className="inline-flex items-center gap-1.5 text-sm">
              Target
              <select data-testid="rail-target" value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} className="bg-black/40 border border-white/10 rounded px-2 py-1 text-xs">
                {TARGETS.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </label>
            <label className="inline-flex items-center gap-1.5 text-sm">
              Order <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="w-20 bg-black/40 border border-white/10 rounded px-2 py-1 text-xs" />
            </label>
            <label className="inline-flex items-center gap-1.5 text-sm" title="Hide this rail from free-tier viewers">
              <input data-testid="rail-paid" type="checkbox" checked={!!form.is_paid_only} onChange={(e) => setForm({ ...form, is_paid_only: e.target.checked })} className="accent-tv-accent" />
              <Lock className="w-3 h-3" /> Paid only
            </label>
            <label className="inline-flex items-center gap-1.5 text-sm" title="Show in Kids mode">
              <input data-testid="rail-kids-safe" type="checkbox" checked={!!form.is_kids_safe} onChange={(e) => setForm({ ...form, is_kids_safe: e.target.checked })} className="accent-emerald-400" />
              🧒 Kids-safe
            </label>
          </div>
          <input placeholder="Search videos…" value={search} onChange={(e) => setSearch(e.target.value)} className={inputCls} />
          <div className="max-h-52 overflow-y-auto space-y-0.5 border border-white/10 rounded p-1 bg-black/40">
            {filteredVideos.length === 0 && <div className="text-white/40 text-xs italic p-2">No videos match.</div>}
            {filteredVideos.map((v) => {
              const sel = form.video_ids.includes(v.id);
              return (
                <label key={v.id} className="flex items-center gap-2 text-xs text-white/80 bg-white/5 rounded px-1.5 py-1 cursor-pointer hover:bg-white/10">
                  <input
                    type="checkbox" checked={sel}
                    onChange={(e) => setForm((f) => ({
                      ...f,
                      video_ids: e.target.checked ? [...f.video_ids, v.id] : f.video_ids.filter((x) => x !== v.id),
                    }))}
                    className="accent-tv-accent"
                  />
                  <span className="truncate flex-1">{v.title}</span>
                  <span className="text-white/40">{v.category || v.kind || ""}</span>
                </label>
              );
            })}
          </div>
          <div className="flex gap-2">
            <button data-testid="rail-save" onClick={create} className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-black font-black text-sm py-2 rounded">Save rail · {form.video_ids.length} video{form.video_ids.length === 1 ? "" : "s"}</button>
            <button onClick={() => { setAdding(false); setForm(emptyRail()); }} className="bg-white/5 hover:bg-white/15 text-white/70 px-3 py-2 rounded">Cancel</button>
          </div>
        </div>
      )}

      {loading ? <div className="text-white/60">Loading…</div> : (
        <div className="space-y-2">
          {rails.length === 0 && <div className="text-white/40 text-center py-6 text-sm">No featured rails yet.</div>}
          {rails.map((r) => (
            <div key={r.id} data-testid={`admin-tv-rail-${r.id}`} className="bg-white/5 border border-white/10 rounded-lg p-3">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-bold text-lg flex-1 truncate">{r.title}</div>
                <span className="text-[9px] bg-white/10 text-white/70 px-1.5 py-0.5 rounded uppercase font-black tracking-wider">{r.kind || "regular"}</span>
                <span className="text-[9px] bg-white/10 text-white/70 px-1.5 py-0.5 rounded uppercase font-black tracking-wider">{r.target || "both"}</span>
                {r.is_paid_only && <span className="inline-flex items-center gap-0.5 text-[9px] bg-amber-500/25 text-amber-200 border border-amber-500/40 px-1.5 py-0.5 rounded uppercase font-black tracking-wider"><Lock className="w-2.5 h-2.5" /> Paid</span>}
                <span className="text-white/40 text-xs">{(r.videos || []).length} videos · order {r.order}</span>
                <label className="inline-flex items-center gap-1 text-xs">
                  <input type="checkbox" checked={!!r.is_active} onChange={(e) => patch(r, { is_active: e.target.checked })} className="accent-tv-accent" /> active
                </label>
                <label className="inline-flex items-center gap-1 text-xs" title="Paid-only">
                  <input type="checkbox" checked={!!r.is_paid_only} onChange={(e) => patch(r, { is_paid_only: e.target.checked })} className="accent-amber-500" /> <Lock className="w-3 h-3" />
                </label>
                <button onClick={() => patch(r, { order: (r.order || 0) - 1 })} className="p-1 hover:bg-white/10 rounded"><ArrowUp className="w-3.5 h-3.5" /></button>
                <button onClick={() => patch(r, { order: (r.order || 0) + 1 })} className="p-1 hover:bg-white/10 rounded"><ArrowDown className="w-3.5 h-3.5" /></button>
                <button onClick={() => del(r)} className="p-1 text-red-300 hover:text-red-100"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
              {r.subtitle && <div className="text-white/60 text-xs mt-0.5">{r.subtitle}</div>}
              <div className="flex items-center gap-1.5 mt-2 overflow-x-auto pb-1">
                {(r.videos || []).map((v) => (
                  <div key={v.id} className="shrink-0 w-40 bg-black/40 border border-white/10 rounded p-1.5">
                    <div className="aspect-video bg-black rounded overflow-hidden flex items-center justify-center text-white/30">
                      {v.thumbnail_url
                        ? <img src={v.thumbnail_url} alt="" className="w-full h-full object-cover" />
                        : <Film className="w-5 h-5" />}
                    </div>
                    <div className="mt-1 text-xs text-white/90 truncate">{v.title}</div>
                    <button
                      onClick={() => patch(r, { video_ids: (r.video_ids || []).filter((x) => x !== v.id) })}
                      className="mt-1 text-[10px] text-red-300 hover:text-red-100 inline-flex items-center gap-1"
                    ><X className="w-3 h-3" /> remove</button>
                  </div>
                ))}
                {(r.videos || []).length === 0 && <div className="text-white/40 italic text-sm p-3">Empty rail — edit this rail to add videos.</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const inputCls = "w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-white text-sm focus:border-tv-accent outline-none";
const emptyHero = () => ({ title: "", subtitle: "", description: "", image_url: "", trailer_url: "", watch_url: "", info_url: "", badge: "", target: "both", order: 0, is_active: true, is_paid_only: false, is_kids_safe: false });
const emptyRail = () => ({ title: "", subtitle: "", video_ids: [], target: "both", is_active: true, order: 0, kind: "regular", is_paid_only: false, cover_image_url: "", brand_color: "", is_kids_safe: false });
const emptyCS = () => ({ title: "", subtitle: "", poster_url: "", trailer_url: "", watch_url: "", release_at: futureIso(14), is_paid_only: false, is_kids_safe: false, target: "both", order: 0, is_active: true });
function futureIso(days) { const d = new Date(); d.setDate(d.getDate() + days); d.setMinutes(0, 0, 0); return d.toISOString(); }

// ── Coming Soon pane ────────────────────────────────────────────────────
function ComingSoonPane() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(emptyCS());

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/tv/coming-soon");
      setRows(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    if (!form.release_at) { toast.error("Release date required"); return; }
    try {
      await api.post("/admin/tv/coming-soon", { ...form, order: Number(form.order) || 0 });
      toast.success("Drop scheduled"); setAdding(false); setForm(emptyCS()); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const patch = async (row, body) => {
    try { await api.patch(`/admin/tv/coming-soon/${row.id}`, body); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (row) => {
    if (!window.confirm(`Delete "${row.title}"?`)) return;
    try { await api.delete(`/admin/tv/coming-soon/${row.id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-3" data-testid="admin-tv-cs-pane">
      <div className="flex items-center gap-2">
        <div className="text-white/60 text-sm flex-1">Coming Soon drops show on TV home with a live countdown + "Remind me" CTA.</div>
        <button
          data-testid="admin-tv-cs-add-open"
          onClick={() => setAdding((v) => !v)}
          className="bg-tv-accent hover:bg-yellow-400 text-black font-black text-xs px-3 py-1.5 rounded inline-flex items-center gap-1"
        ><Plus className="w-3.5 h-3.5" /> Schedule drop</button>
      </div>

      {adding && (
        <div className="bg-white/5 border border-white/10 rounded-lg p-3 space-y-2" data-testid="admin-tv-cs-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <input data-testid="cs-title" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} />
            <input placeholder="Subtitle" value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} className={inputCls} />
            <input data-testid="cs-poster" placeholder="Poster URL (2:3 portrait recommended)" value={form.poster_url} onChange={(e) => setForm({ ...form, poster_url: e.target.value })} className={inputCls} />
            <input placeholder="Trailer URL (optional)" value={form.trailer_url} onChange={(e) => setForm({ ...form, trailer_url: e.target.value })} className={inputCls} />
            <input placeholder="Watch URL (added once released)" value={form.watch_url} onChange={(e) => setForm({ ...form, watch_url: e.target.value })} className={inputCls} />
            <label className="block">
              <div className="text-[9px] uppercase text-white/50 font-black mb-0.5 flex items-center gap-1"><Clock className="w-3 h-3" /> Release at (UTC)</div>
              <input
                data-testid="cs-release-at"
                type="datetime-local"
                value={form.release_at.slice(0, 16)}
                onChange={(e) => setForm({ ...form, release_at: e.target.value + ":00Z" })}
                className={inputCls}
              />
            </label>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <label className="inline-flex items-center gap-1.5 text-sm">
              Target
              <select value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} className="bg-black/40 border border-white/10 rounded px-2 py-1 text-xs">
                {TARGETS.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </label>
            <label className="inline-flex items-center gap-1.5 text-sm">
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="accent-tv-accent" /> Active
            </label>
            <label className="inline-flex items-center gap-1.5 text-sm">
              <input data-testid="cs-paid" type="checkbox" checked={!!form.is_paid_only} onChange={(e) => setForm({ ...form, is_paid_only: e.target.checked })} className="accent-tv-accent" />
              <Lock className="w-3 h-3" /> Paid only
            </label>
            <label className="inline-flex items-center gap-1.5 text-sm">
              <input data-testid="cs-kids-safe" type="checkbox" checked={!!form.is_kids_safe} onChange={(e) => setForm({ ...form, is_kids_safe: e.target.checked })} className="accent-emerald-400" />
              🧒 Kids-safe
            </label>
            <button data-testid="cs-save" onClick={create} className="ml-auto bg-emerald-500 hover:bg-emerald-400 text-black font-black text-sm px-4 py-1.5 rounded">Save drop</button>
            <button onClick={() => { setAdding(false); setForm(emptyCS()); }} className="bg-white/5 hover:bg-white/15 text-white/70 text-sm px-3 py-1.5 rounded">Cancel</button>
          </div>
        </div>
      )}

      {loading ? <div className="text-white/60">Loading…</div> : (
        <div className="space-y-2">
          {rows.length === 0 && <div className="text-white/40 text-center py-6 text-sm">No drops scheduled yet.</div>}
          {rows.map((r) => (
            <div key={r.id} data-testid={`admin-tv-cs-${r.id}`} className="bg-white/5 border border-white/10 rounded-lg p-3 flex items-center gap-3">
              <div className="w-16 h-20 bg-black rounded overflow-hidden shrink-0 flex items-center justify-center text-white/30">
                {r.poster_url
                  ? <img src={r.poster_url} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                  : <ImageIcon className="w-5 h-5" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <div className="font-bold truncate">{r.title}</div>
                  {r.is_paid_only && <span className="inline-flex items-center gap-0.5 text-[9px] bg-amber-500/25 text-amber-200 border border-amber-500/40 px-1.5 py-0.5 rounded uppercase font-black tracking-wider"><Lock className="w-2.5 h-2.5" /> Paid</span>}
                </div>
                <div className="text-white/50 text-xs truncate">{r.subtitle}</div>
                <div className="text-white/70 text-[11px] mt-0.5 inline-flex items-center gap-1">
                  <Clock className="w-3 h-3" /> {new Date(r.release_at).toLocaleString()} · <Countdown date={r.release_at} />
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <label className="inline-flex items-center gap-1 text-xs">
                  <input type="checkbox" checked={!!r.is_active} onChange={(e) => patch(r, { is_active: e.target.checked })} className="accent-tv-accent" /> on
                </label>
                <button data-testid={`admin-tv-cs-del-${r.id}`} onClick={() => del(r)} className="p-1 text-red-300 hover:text-red-100"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Countdown({ date }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 60_000); return () => clearInterval(t); }, []);
  const ms = new Date(date).getTime() - now;
  if (ms <= 0) return <span className="text-emerald-300 font-bold">available now</span>;
  const days = Math.floor(ms / 86_400_000);
  const hours = Math.floor((ms % 86_400_000) / 3_600_000);
  if (days > 0) return <span className="text-tv-accent font-bold">in {days}d {hours}h</span>;
  const mins = Math.floor((ms % 3_600_000) / 60_000);
  return <span className="text-tv-accent font-bold">in {hours}h {mins}m</span>;
}

